"use client";

import { useEffect, useState, useCallback } from "react";
import { useTranslations } from "next-intl";
import { getPhotoList, getVideoList } from "../../services";
import SlideViewMode from "./SlideViewMode";
import type { PhotoItemType, VideoItemType } from "./types";
import { Loader } from "../loader";
import { getConvertedPhotoItems, getConvertedVideoItems } from "./helpers";

interface MultiReservationSlideViewProps {
  reservationIds: string[];
  imageCount: number;
  showVideos: boolean;
  onClose: () => void;
  refreshIntervalMs?: number;
}

export default function MultiReservationSlideView({
  reservationIds,
  imageCount,
  showVideos,
  onClose,
  refreshIntervalMs = 10000,
}: MultiReservationSlideViewProps) {
  const t = useTranslations();
  const [allItems, setAllItems] = useState<(PhotoItemType | VideoItemType)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      const results = await Promise.all(
        reservationIds.map(async (resId) => {
          if (showVideos) {
            const [photoRes, videoRes] = await Promise.allSettled([
              getPhotoList(resId, false),
              getVideoList(resId),
            ]);

            const rawPhotoData =
              photoRes.status === "fulfilled" ? photoRes.value : null;
            const rawVideoData =
              videoRes.status === "fulfilled" ? videoRes.value : [];

            // getPhotoList returns { photos: [...] } or an array directly
            const rawPhotos = Array.isArray(rawPhotoData)
              ? rawPhotoData
              : rawPhotoData?.photos || [];
            const rawVideos = Array.isArray(rawVideoData)
              ? rawVideoData
              : [];

            const photos: PhotoItemType[] = getConvertedPhotoItems(rawPhotos);
            const videos: VideoItemType[] = getConvertedVideoItems(rawVideos);

            return [...photos, ...videos];
          } else {
            const rawPhotoData = await getPhotoList(resId, false);
            const rawPhotos = Array.isArray(rawPhotoData)
              ? rawPhotoData
              : rawPhotoData?.photos || [];
            const photos: PhotoItemType[] = getConvertedPhotoItems(rawPhotos);
            return photos;
          }
        })
      );

      const merged = results.flat();

      merged.sort((a, b) => {
        const timeA = a.date ? new Date(a.date).getTime() : 0;
        const timeB = b.date ? new Date(b.date).getTime() : 0;
        return timeB - timeA;
      });

      setAllItems(merged);
      setError(null);
    } catch (err) {
      console.error("Error fetching multi-reservation media:", err);
      setError(t("Toast.errorWhileLoadingMedia") || "Error loading media");
    } finally {
      setLoading(false);
    }
  }, [reservationIds, showVideos, t]);

  useEffect(() => {
    if (reservationIds.length === 0) return;

    fetchAll();

    const interval = setInterval(fetchAll, refreshIntervalMs);
    return () => clearInterval(interval);
  }, [reservationIds, fetchAll, refreshIntervalMs]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black z-[9999] flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="fixed inset-0 bg-black z-[9999] flex items-center justify-center cursor-pointer"
        onClick={onClose}
      >
        <p className="text-white text-xl">{error}</p>
      </div>
    );
  }

  if (allItems.length === 0) {
    return (
      <div
        className="fixed inset-0 bg-black z-[9999] flex items-center justify-center cursor-pointer"
        onClick={onClose}
      >
        <p className="text-white text-xl">
          {t("Toast.errorWhileLoadingMedia") || "No media items"}
        </p>
      </div>
    );
  }

  return (
    <SlideViewMode
      items={allItems}
      imageCount={imageCount}
      onClose={onClose}
      showVideos={showVideos}
    />
  );
}